import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { LoadingButton, Spinner } from "components/ui";
import "react-phone-input-2/lib/style.css";
import { translateUsingBrowser } from "utils/misc";
import { Formik } from "formik";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FormLabel from "@mui/material/FormLabel";
import { resendCodeToContactMethod } from "actions/multifactor";
import { loginMFA } from "actions/auth";
import { NotificationManager } from "react-notifications";
import propTypes from "prop-types";

const MultifactorModal = ({ userMFADetails }) => {
  const dispatch = useDispatch();
  // Backend now auto-sends the code on login — start directly at code entry
  const contactMethod = userMFADetails.mfa_delivered_by || "Email";
  const [resendFetching, setResendFetching] = useState(false);
  const [emailResendFetching, setEmailResendFetching] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);

  useEffect(() => {
    if (!timeLeft) return;

    const intervalId = setInterval(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timeLeft]);

  const handleInputChange = (event, setFieldValue) => {
    let { name, value } = event.target;
    setFieldValue(name, value);
  };

  const resendCodeToContactMethodApi = async () => {
    try {
      const response = await dispatch(
        resendCodeToContactMethod(
          contactMethod,
          userMFADetails.MFAUnmaskedEmail,
          userMFADetails.mfa_pending_token
        )
      );
      if (response) {
        NotificationManager.success(
          translateUsingBrowser("verificationCodeSent")
        );
        setTimeLeft(120);
      }
    } finally {
      setResendFetching(false);
    }
  };

  const resendViaEmailApi = async () => {
    setEmailResendFetching(true);
    try {
      const response = await dispatch(
        resendCodeToContactMethod(
          "Email",
          userMFADetails.MFAUnmaskedEmail,
          userMFADetails.mfa_pending_token
        )
      );
      if (response) {
        NotificationManager.success(translateUsingBrowser("codeSentToEmail"));
      }
    } finally {
      setEmailResendFetching(false);
    }
  };

  const deliveryMessage = contactMethod === "SMS"
    ? `Code sent via text to ${userMFADetails.MFAPhoneNumber || "your phone"}`
    : `Code sent to ${userMFADetails.MFAUnmaskedEmail || "your email"}`;


  return (
    <div className="login-screen multifactor" role="main">
      <div className="login-container multifactor-container">
        <div className="login-form-card multifactor-card" role="form">
          <h1 className="login-card-header" id="mfa-title">
            {translateUsingBrowser("additionalSecurityVerification")}
          </h1>
          <p className="multifactor-delivery-message">{deliveryMessage}</p>
          <Formik
              initialValues={{
                verification_code: ""
              }}
              onSubmit={async ({ verification_code }) => {
                // S3DEV2-7710: return sign_in's pending token with the code.
                await dispatch(loginMFA(verification_code, userMFADetails.mfa_pending_token));
              }}
            >
              {({ isSubmitting, handleSubmit, values, setFieldValue }) => (
                <form className="multifactor-form" onSubmit={handleSubmit} aria-labelledby="mfa-title">
                  <FormLabel className="multifactor-step-label" id="step2-label">
                    {translateUsingBrowser("step2EnterCode")}
                  </FormLabel>
                  <div className="form-field">
                    <TextField
                      id="verification_code"
                      name="verification_code"
                      type="text"
                      value={values.verification_code || ""}
                      onChange={e => {
                        return handleInputChange(e, setFieldValue);
                      }}
                      className="verification-code"
                      aria-labelledby="step2-label"
                    />

                  </div>
                  {isSubmitting ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 120
                      }}
                      role="status"
                      aria-label="Verifying code"
                    >
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      <div className="login-form-actions multifactor-row-actions">
                        <FormLabel className="multifactor-step-label" id="resend-label">
                          {translateUsingBrowser("didntReceiveCode")}
                        </FormLabel>

                        {timeLeft == 0 && (
                          <LoadingButton
                            color="primary"
                            type="button"
                            isFetching={resendFetching}
                            onClick={() => {
                              setResendFetching(true);
                              resendCodeToContactMethodApi();
                            }}
                          >
                            {translateUsingBrowser("resend")}
                          </LoadingButton>
                        )}
                        {timeLeft > 0 && (
                          <FormLabel className="multifactor-step-label" role="status" aria-live="polite">
                            {translateUsingBrowser("waiting").replace(
                              "X",
                              timeLeft
                            )}
                          </FormLabel>
                        )}
                      </div>
                      {contactMethod === "SMS" && userMFADetails.MFAUnmaskedEmail && (
                        <div className="login-form-actions multifactor-row-actions">
                          <LoadingButton
                            color="secondary"
                            type="button"
                            isFetching={emailResendFetching}
                            onClick={resendViaEmailApi}
                          >
                            {translateUsingBrowser("sendViaEmailInstead")}
                          </LoadingButton>
                        </div>
                      )}
                      <div className="login-form-actions multifactor-column-actions">
                        <Button
                          variant="contained"
                          color="primary"
                          id="verify-button"
                          type="submit"
                          disabled={
                            !values.verification_code ||
                            values.verification_code.length !== 6
                          }
                        >
                          {translateUsingBrowser("verify")}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </Formik>
        </div>
      </div>
    </div>
  );
};

MultifactorModal.propTypes = {
  userMFADetails: propTypes.object
};

export default MultifactorModal;
